import * as React from "react";
import { useTranslation } from "react-i18next";
import BottomSheet from "Components/BottomSheet";
import SignatureCanvas from "react-signature-canvas";
import { useRef } from "react";
import Icon from "Components/Icon";

interface IProps {
    title: string
    loading: boolean
    onCapture: (image: string, fileType: string, onComplete: () => void) => void
}

const Signature = (props: IProps) => {
    const [t] = useTranslation()
    const [open, setOpen] = React.useState(false);
    const [type, setType] = React.useState<'DRAW' | 'UPLOAD'>('DRAW');
    const [fileContent, setFileContent] = React.useState<string>('');
    const [fileType, setFileType] = React.useState<string>('');
    const sigCanvas = useRef<SignatureCanvas>(null);

    const handleCapture = () => {
        if (type == 'DRAW') {
            const signature = sigCanvas?.current?.toDataURL("image/png")
            if (signature)
                props.onCapture(signature, "image/png", () => {
                    setOpen(false);
                })
        } else {
            if (fileContent)
                props.onCapture(fileContent, fileType, () => {
                    setOpen(false);
                })
        }
    };

    const handleClearCapture = () => {
        setFileContent('')
        setFileType('')
        sigCanvas?.current?.clear()
    };

    const handleSelect = () => {
        const fileInput = document.getElementById(`fileInput_Signature`)
        fileInput?.click()
    };

    const handleFileChange = (e: any) => {
        const file = e.target.files[0]
        if (file) {
            const reader = new FileReader()
            reader.onloadend = () => {
                const base64String = reader.result as string
                setFileContent(base64String)
                setFileType(file.type)
            }
            reader.readAsDataURL(file)
        }
    };

    const handleClickOpen = (e: any) => {
        e.preventDefault();
        setOpen(true);
    };

    const handleClose = (data: any) => {
        setOpen(false)
    };

    return (
        <React.Fragment>
            <div style={{ padding: "10px" }}>
                <button className="btn btn-primary btn-lg flex gap-2 items-center justify-center w-full" onClick={handleClickOpen}>
                    {t("CREATE_SIGNATURE")}
                </button>
            </div>
            <BottomSheet
                title={props.title}
                visible={open}
                onCancel={() => handleClose(null)}>
                <div
                    style={{
                        width: 50,
                        height: 6,
                        backgroundColor: "#ccc",
                        borderRadius: 3,
                        position: "absolute",
                        top: 8,
                        left: "50%",
                        transform: "translateX(-50%)",
                    }} />
                <div className={`overflow-auto px-6 pb-6${(props.loading ? " loading" : "")}`}>

                    <div className="flex" style={{ marginTop: "8px", marginBottom: "8px" }}>
                        <div className="flex items-center me-4 mb-3">
                            <input
                                id="drawSignatureRadio"
                                name="drawSignatureRadio"
                                type="radio"
                                value="DRAW"
                                checked={type == "DRAW"}
                                onChange={(e) => setType('DRAW')}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
                            <label htmlFor="drawSignatureRadio" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300" style={{ marginRight: "8px" }}>{String(t("DRAW_SIGNATURE"))}</label>
                        </div>
                        <div className="flex items-center me-4 mb-3" style={{ marginRight: "10px" }}>
                            <input
                                id="uploadSignatureRadio"
                                name="uploadSignatureRadio"
                                type="radio"
                                value="UPLOAD"
                                checked={type == "UPLOAD"}
                                onChange={(e) => setType('UPLOAD')}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
                            <label htmlFor="uploadSignatureRadio" className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300" style={{ marginRight: "8px" }}>{String(t("UPLOAD_SIGNATURE"))}</label>
                        </div>
                    </div>

                    {type == "DRAW" ?
                        <div className='flex justify-center items-center'>
                            <SignatureCanvas
                                ref={sigCanvas}
                                penColor="black"
                                clearOnResize={true}
                                canvasProps={{ width: 250, height: 250, className: 'sigCanvas border border-primary border-dashed rounded-lg mr-4 ml-4', }}
                                backgroundColor="white"
                            />
                        </div>
                        :
                        (fileContent ?
                            <div className='flex justify-center items-center'>
                                <div className="flex flex-col justify-between w-40 h-36 rounded-lg border border-dashed border-gray-500 bg-no-repeat bg-center overflow-hidden"
                                    style={{ backgroundImage: `url(${fileContent})`, backgroundSize: "contain" }}>
                                </div>
                            </div>
                            :
                            <button className="btn btn-inherit btn-lg flex gap-2 items-center justify-center w-full"
                                onClick={() => handleSelect()}
                                style={{ paddingLeft: 0, paddingRight: 0 }}>
                                <Icon name="document-download" className="w-6 h-6" />
                                <input
                                    id="fileInput_Signature"
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={handleFileChange} />
                                {t("FROM_FILE")}
                            </button>)
                    }
                    <div className="mt-3">
                        <div id="alert-warning" className="flex items-center p-3 text-white rounded-xl bg-gray-800 dark:bg-gray-800"
                            role="alert">
                            <div className="bg-warning/[.16] text-warning p-2 rounded-lg flex justify-center items-center">
                                <Icon name="ic_warning" className="w-6 h-6" />
                            </div>
                            <span className="sr-only">info</span>
                            <div className="ms-3 text-sm font-semibold grow">{t('SIGNATURE_WARNING_MESSAGE')}</div>
                        </div>
                    </div>
                </div>
                <div className="cta" style={{ padding: "10px" }}>
                    <div className='flex justify-between items-center gap-2'>
                        <button className="btn btn-warning btn-lg flex gap-2 items-center justify-center w-full" onClick={handleClearCapture}>
                            {t("CLEAR")}
                        </button>
                        <button className="btn btn-primary btn-lg flex gap-2 items-center justify-center w-full" onClick={handleCapture}>
                            {t("SAVE")}
                        </button>
                    </div>
                </div>
            </BottomSheet>
        </React.Fragment>)
}

export default Signature;
